import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(urlKeyword);

  usePageMeta({
    title: urlKeyword ? `搜索「${urlKeyword}」- 生活中的玛丽` : "站内搜索 - 生活中的玛丽",
    description: "在生活中的玛丽站内搜索影视作品，输入关键词即可快速定位电影与电视剧资讯、剧情与影评。",
    keywords: "生活中的玛丽,站内搜索,影视搜索,电影搜索,电视剧搜索",
  });

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = keyword.trim();
      if (next === urlKeyword) return;
      setSearchParams(next ? { q: next } : {}, { replace: true });
    }, 350);
    return () => window.clearTimeout(t);
  }, [keyword, urlKeyword, setSearchParams]);

  const results = useMemo(() => {
    const q = urlKeyword.trim().toLowerCase();
    if (!q) return [];
    return movies.filter((m) => {
      return (
        m.title.toLowerCase().includes(q) ||
        m.genres.some((g) => g.toLowerCase().includes(q)) ||
        m.region.toLowerCase().includes(q) ||
        m.director.toLowerCase().includes(q) ||
        m.cast.some((c) => c.toLowerCase().includes(q))
      );
    });
  }, [urlKeyword]);

  const recommended = useMemo(() => [...movies].sort((a, b) => b.rating - a.rating).slice(0, 4), []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(keyword.trim() ? { q: keyword.trim() } : {});
  };

  return (
    <div className="w-full">
      <div className="bg-primary-500">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: "站内搜索" }]} />
          </div>
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-background-50">搜索影视内容</h1>
          <form onSubmit={submit} className="mt-6 max-w-2xl">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-lg"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入影视名称、类型、导演或演员"
                className="w-full h-12 md:h-14 pl-11 pr-28 rounded-md bg-background-50 text-foreground-950 text-sm md:text-base placeholder:text-foreground-400 outline-none border border-background-50 focus:border-accent-300"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 md:h-11 px-4 md:px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {urlKeyword ? (
          <p className="text-sm text-foreground-600 mb-6">
            关键词「<span className="text-accent-600 font-medium">{urlKeyword}</span>」共找到{" "}
            <strong className="text-foreground-950 font-semibold">{results.length}</strong> 部相关作品
          </p>
        ) : (
          <p className="text-sm text-foreground-600 mb-6">输入关键词开始搜索吧，比如「悬疑」「科幻」或作品名称。</p>
        )}

        {urlKeyword && results.length === 0 ? (
          <div className="space-y-10">
            <div className="bg-background-50 rounded-lg border border-background-200">
              <EmptyState
                icon="ri-search-eye-line"
                title="没有找到相关影视作品"
                description="换个关键词试试，或者看看下面这些高口碑推荐。"
              />
            </div>
            <section>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                <h2 className="font-heading font-bold text-xl text-foreground-950">为你推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {recommended.map((m) => (
                  <MovieCard key={m.id} movie={m} showType />
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="space-y-3">
            {results.map((m) => (
              <Link
                key={m.id}
                to={`/detail/${m.id}`}
                className="group flex gap-4 p-3 md:p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-[80px] h-[114px] md:w-[100px] md:h-[142px] shrink-0 rounded-md overflow-hidden bg-background-100">
                  <img
                    src={m.cover}
                    alt={`${m.title} 搜索结果封面`}
                    title={`${m.title} - 影视详情`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex-1 min-w-0 py-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-[11px] font-medium">
                      {m.type === "movie" ? "电影" : "电视剧"}
                    </span>
                    <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-[11px] font-medium">
                      {m.year}
                    </span>
                    <span className="text-xs text-foreground-500">{m.region}</span>
                  </div>
                  <h3 className="mt-2.5 font-heading font-bold text-base md:text-lg text-foreground-950 group-hover:text-primary-500 transition-colors">
                    {m.title}
                  </h3>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {m.synopsis}
                  </p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      {m.rating.toFixed(1)}
                    </span>
                    <span className="hidden sm:flex items-center gap-1">
                      <i className="ri-price-tag-3-line"></i>
                      {m.genres.join(" / ")}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}